/**
 * PLANNING bounded context.
 *
 * The canonical authoring shape is Plan -> ordered Sections (each with a
 * repeatCount) -> ordered Intervals, where an interval owns BOTH its work and
 * its trailing rest. The runtime never sees that tree: `deriveSessionPlan`
 * flattens it into an ordered segment list, expanding repeats, numbering boxing
 * rounds across the whole plan, and synthesizing recovery segments. Keeping the
 * flattening here — rather than in the engine — is what lets the builder, the
 * iPad tape, the start bar, and the live session all agree on one duration and
 * one round count without recomputing it three different ways.
 *
 * Durations are whole milliseconds everywhere. The Swift app authors them in
 * seconds through steppers; the persistence layer is responsible for the unit
 * conversion at the boundary so nothing above this file has to think about it.
 */

import type { Milliseconds } from "./time";

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

/**
 * Every identifier that outlives a render is branded. Plan, section, and
 * interval ids are persisted product data — a section id is an input to the
 * derived-segment hash below, so a mistyped id would silently change runtime
 * segment identity rather than fail to compile.
 */
declare const workoutPlanIdBrand: unique symbol;
declare const workoutSectionIdBrand: unique symbol;
declare const workoutIntervalIdBrand: unique symbol;
declare const sessionSegmentIdBrand: unique symbol;
declare const drillIdBrand: unique symbol;
declare const activityIdBrand: unique symbol;

export type WorkoutPlanId = string & { readonly [workoutPlanIdBrand]: true };
export type WorkoutSectionId = string & { readonly [workoutSectionIdBrand]: true };
export type WorkoutIntervalId = string & { readonly [workoutIntervalIdBrand]: true };
export type SessionSegmentId = string & { readonly [sessionSegmentIdBrand]: true };
/** Drill ids are stable slugs, not UUIDs, and are stored on intervals. */
export type DrillId = string & { readonly [drillIdBrand]: true };
/** Effort activity ids are UUIDs; the five catalog values are fixed forever. */
export type ActivityId = string & { readonly [activityIdBrand]: true };

export function workoutPlanId(raw: string): WorkoutPlanId {
  return raw as WorkoutPlanId;
}

export function workoutSectionId(raw: string): WorkoutSectionId {
  return raw as WorkoutSectionId;
}

export function workoutIntervalId(raw: string): WorkoutIntervalId {
  return raw as WorkoutIntervalId;
}

export function sessionSegmentId(raw: string): SessionSegmentId {
  return raw as SessionSegmentId;
}

export function drillId(raw: string): DrillId {
  return raw as DrillId;
}

export function activityId(raw: string): ActivityId {
  return raw as ActivityId;
}

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export type WorkoutMode = "simple" | "guided";

export const WORKOUT_MODE_TITLE: Readonly<Record<WorkoutMode, string>> = Object.freeze({
  simple: "Simple timer",
  guided: "Guided workout",
});

export type SegmentKind =
  | "preparation"
  | "warmup"
  | "work"
  | "recovery"
  | "strength"
  | "cooldown";

export const SEGMENT_KIND_TITLE: Readonly<Record<SegmentKind, string>> = Object.freeze({
  preparation: "Get ready",
  warmup: "Warm up",
  work: "Work",
  recovery: "Recovery",
  strength: "Strength",
  cooldown: "Cool down",
});

/** Uppercase rail labels; the live screen shows these, never the title case. */
export const SEGMENT_KIND_SHORT_LABEL: Readonly<Record<SegmentKind, string>> = Object.freeze({
  preparation: "READY",
  warmup: "WARM UP",
  work: "BOX",
  recovery: "ACTIVE REST",
  strength: "CORE",
  cooldown: "COOL DOWN",
});

/**
 * Older releases wrote different display strings for three kinds. Decoding must
 * still accept them, so the legacy spelling is part of the domain vocabulary
 * rather than a private detail of the store.
 */
export const LEGACY_SEGMENT_KIND_ENCODING: Readonly<Record<SegmentKind, string>> = Object.freeze({
  preparation: "Get ready",
  warmup: "Ramp up",
  work: "Work",
  recovery: "Active rest",
  strength: "Core + strength",
  cooldown: "Cool down",
});

/**
 * A segment's role decides whether it counts as a numbered boxing round, and
 * therefore whether the live screen calls combos over it. It is deliberately
 * separate from the kind: a `.work` interval built from a catalog drill is
 * still work, but it is not a round.
 */
export type SegmentRole = "phase" | "boxingRound" | "standaloneDrill";

export type ComboSource = "generated" | "saved" | "mixed";

/** `comboSource` is optional on persisted plans for migration; nil resolves here. */
export const DEFAULT_COMBO_SOURCE: ComboSource = "mixed";

/**
 * The closed vocabulary of guided call families a boxing round can request.
 *
 * PLANNING owns this even though the recipe each case expands into is combo
 * knowledge, because the case name is what a plan persists (`WorkoutModels.swift`
 * declares the same enum beside the interval that stores it). Putting the enum
 * in COMBAT would invert the dependency: an authored plan would then be
 * unreadable without loading the generator it merely names. COMBAT imports this
 * and maps each case to a full parameter recipe, so the edge runs one way.
 *
 * Raw values are the persisted spelling and must never be renamed.
 */
export type GuidedComboTheme =
  | "straightShots"
  | "leadHook"
  | "bodyWork"
  | "blocks"
  | "slips"
  | "uppercuts"
  | "fastHands"
  | "rolls"
  | "powerAccents"
  | "freestyle";

/** Declaration order of the Swift `CaseIterable` conformance, which drives pickers. */
export const GUIDED_COMBO_THEME_ORDER: readonly GuidedComboTheme[] = Object.freeze([
  "straightShots",
  "leadHook",
  "bodyWork",
  "blocks",
  "slips",
  "uppercuts",
  "fastHands",
  "rolls",
  "powerAccents",
  "freestyle",
] as const);

/**
 * A round authored before themes existed, or decoded from a corrupted value,
 * falls back to the case that constrains the generator least — an unrecognised
 * theme must never silently narrow a user's calls.
 */
export const DEFAULT_GUIDED_COMBO_THEME: GuidedComboTheme = "freestyle";

export function isGuidedComboTheme(value: string): value is GuidedComboTheme {
  return (GUIDED_COMBO_THEME_ORDER as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// Interval dosage
// ---------------------------------------------------------------------------

/**
 * Timed and rep targets are a union rather than an optional count, because
 * switching an interval back to Timed must ERASE the rep target — with an
 * optional field that erasure is a convention someone can forget, here it is
 * the only representable outcome. A rep interval still carries a work duration:
 * it is a visible time cap, and the app never counts repetitions itself.
 */
export type IntervalTarget =
  | { readonly mode: "timed" }
  | { readonly mode: "repetitions"; readonly repetitions: number };

export const TIMED_TARGET: IntervalTarget = Object.freeze({ mode: "timed" } as const);

/** Switching to Reps seeds 20 rather than starting the stepper at its minimum. */
export const SEEDED_REPETITION_TARGET = 20;

export function repetitionTarget(repetitions: number): IntervalTarget {
  return { mode: "repetitions", repetitions };
}

/**
 * Rest belongs to the activity above it. Modelling "no rest" as a variant
 * instead of `restDuration === 0` keeps the rest title/detail/drill from
 * lingering as unreachable data on an interval that has no recovery at all.
 */
export type IntervalRest =
  | { readonly kind: "none" }
  | {
      readonly kind: "recovery";
      readonly duration: Milliseconds;
      readonly title?: string;
      readonly detail?: string;
      readonly drill?: string;
    };

export const NO_REST: IntervalRest = Object.freeze({ kind: "none" } as const);

// ---------------------------------------------------------------------------
// Authoring model
// ---------------------------------------------------------------------------

export interface WorkoutInterval {
  readonly id: WorkoutIntervalId;
  readonly title: string;
  /** Coaching note. Empty string, never absent, so editors bind directly. */
  readonly detail: string;
  readonly workDuration: Milliseconds;
  readonly rest: IntervalRest;
  readonly target: IntervalTarget;
  /** Free-text drill label shown on the live screen. */
  readonly drill?: string;
  readonly comboTheme?: GuidedComboTheme;
  /** Catalog drill this interval came from; drives the standaloneDrill role. */
  readonly sourceDrillId?: DrillId;
  /** Explicit role wins over derivation; absent means "derive it". */
  readonly role?: SegmentRole;
  /** Absent means `work`; persisted plans predate the field. */
  readonly kind?: SegmentKind;
}

/**
 * A section always holds at least one interval. The section editor disables
 * Done on an empty list and the interval menu disables Delete at one item, so
 * an empty section is not a state the product can reach — encoding that in the
 * type means the flattener never has to decide what an empty section means.
 */
export type WorkoutIntervalList = readonly [WorkoutInterval, ...WorkoutInterval[]];

export interface WorkoutSection {
  readonly id: WorkoutSectionId;
  readonly name: string;
  /** Clamped to >= 1 on every set and on decode; the UI offers 1...20. */
  readonly repeatCount: number;
  readonly intervals: WorkoutIntervalList;
}

export interface WorkoutPlan {
  /** Reused when editing, so Save overwrites instead of copying. */
  readonly id: WorkoutPlanId;
  readonly name: string;
  readonly mode: WorkoutMode;
  readonly sections: readonly WorkoutSection[];
  readonly halfwayCue: boolean;
  /** The UI writes only 10 s or 0; 0 means the final-ten cue is off. */
  readonly countdownCue: Milliseconds;
  /** How often a boxing interval changes the call. 0 = manual only. */
  readonly drillSwitchInterval: Milliseconds;
  /** Round rhythm. Both values are written together or both are 0. */
  readonly workBurst: Milliseconds;
  readonly burstRest: Milliseconds;
  /** Optional for migration; absent resolves to `mixed`. */
  readonly comboSource?: ComboSource;
}

// ---------------------------------------------------------------------------
// Runtime projection
// ---------------------------------------------------------------------------

/**
 * A flattened, engine-ready unit of work. This is a projection, never authored
 * and never persisted on its own: repeats are already expanded, rest is already
 * its own segment, and boxing rounds are already numbered.
 */
export interface SessionSegment {
  readonly id: SessionSegmentId;
  readonly kind: SegmentKind;
  readonly title: string;
  readonly detail: string;
  readonly duration: Milliseconds;
  /** Consecutive across the whole plan; absent unless this is a boxing round. */
  readonly roundNumber?: number;
  readonly drill?: string;
  readonly comboTheme?: GuidedComboTheme;
  readonly target: IntervalTarget;
  readonly sourceDrillId?: DrillId;
  readonly role: SegmentRole;
  /** Which authored section this occurrence came from, for tape selection. */
  readonly sectionId: WorkoutSectionId;
  /** Which authored interval this occurrence came from. */
  readonly intervalId: WorkoutIntervalId;
}

/**
 * Everything the engine needs to run a workout, with no back-reference into the
 * mutable authoring tree — starting a session takes a snapshot, so editing the
 * plan afterwards cannot retroactively change what is being executed.
 */
export interface SessionPlan {
  readonly planId: WorkoutPlanId;
  readonly name: string;
  readonly mode: WorkoutMode;
  readonly segments: readonly SessionSegment[];
  readonly totalDuration: Milliseconds;
  readonly workRounds: number;
  readonly halfwayCue: boolean;
  readonly countdownCue: Milliseconds;
  readonly drillSwitchInterval: Milliseconds;
  readonly workBurst: Milliseconds;
  readonly burstRest: Milliseconds;
  readonly comboSource: ComboSource;
}

// ---------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------

export const PLAN_NAME_FALLBACK: Readonly<Record<WorkoutMode, string>> = Object.freeze({
  simple: "Custom rounds",
  guided: "Untitled workout",
});

/** A legacy plan decoded from flat segments is wrapped in one section named this. */
export const SECTION_NAME_FALLBACK = "Workout";

export const REST_SEGMENT_FALLBACK = Object.freeze({
  title: "Recover",
  detail: "Breathe and reset",
});

/** The segment a plan with no intervals is replaced by (F-TR-06). Exact
 *  user-facing copy is a domain contract, so it is declared here with the rest
 *  of the planning copy rather than beside the engine code that consumes it. */
export const EMPTY_PLAN_SEGMENT_COPY = Object.freeze({
  title: "Ready",
  detail: "No workout intervals were available.",
});

export const BUILDER_COPY = Object.freeze({
  hubTitle: "Build your training",
  hubSubtitle: "Create reusable workouts and tune the boxing calls used inside them.",
  hubFooter: "Workouts can use generated or saved calls from Combo Lab.",
  savedListDetail:
    "Create a timer in seconds or compose a reusable workout from sections and activities.",
  quickTimerEyebrow: "Start without a plan",
  quickTimerTitle: "Quick timer",
  quickTimerDetail: "One repeatable interval. Every number below has one job.",
  guidedEyebrow: "Reusable workout",
  guidedTitle: "Compose the work",
  guidedDetail:
    "Sections organize your workout. Each section repeats an ordered list of intervals.",
  wideCanvasDetail:
    "Build the ordered session on this canvas. Select a section to edit its intervals without losing the plan around it.",
  inspectorDetail: "The session total and live cue behavior update as you edit the canvas.",
  sectionCanvasTitle: "Workout sections",
  sectionCanvasSubtitle: "Run top to bottom",
  sectionEditorTitle: "Edit section",
  sectionRepeatDetail: "Run this complete activity list again",
  sectionEditorFooter:
    "Rest belongs to the activity above it. When rest ends, the next activity begins.",
  intervalEditorTitle: "Edit interval",
  intervalTimedWorkDetail: "Time performing this activity",
  intervalRepsWorkTitle: "Time cap",
  intervalRepsWorkDetail: "A visible clock; the app does not count reps",
  intervalRepetitionsDetail: "You advance when the target is complete",
  intervalRestDetail: "Recovery before the next activity",
  intervalEditorFooter:
    "Work and rest stay together as one interval. This same model is used everywhere in the workout.",
  comboCadenceDetail: "How often a boxing interval changes the call",
  comboCadenceManual: "Manual only",
  roundRhythmDetail:
    "Alternate effort and active recovery inside each timed boxing interval",
  roundRhythmCaption: "The rhythm repeats without changing the interval's total duration.",
  safetyNote:
    "Clear the floor around you. Stop for pain, dizziness, or unusual symptoms. You can pause, skip, or end at any time.",
  startSimple: "Start timer",
  startGuided: "Start workout",
  saveSuccessTitle: "Workout saved",
  saveSuccessBody: "This workout is now available in My workouts.",
  saveFailureTitle: "Couldn't save workout",
  saveFailureBody:
    "The workout could not be written. Check available storage, then try again.",
  tapeHint:
    "Select an interval to edit it in place. Drag to reorder; Earlier and Later provide the same reversible path.",
  tapeEmptyInspector:
    "Timing, cues, and useful equipment annotations will appear here.",
});

/**
 * Fail-closed messaging. Every one of these says the same thing in different
 * words: the user's saved data was left exactly as it was. That promise is the
 * reason the store refuses unverified writes, so the copy lives beside the
 * model rather than in a view.
 */
export const PLAN_LIBRARY_FAILURE_COPY = Object.freeze({
  unreadableTitle: "Library preserved",
  unreadableBody:
    "Fighting Form could not read the saved-workout file, so it will not overwrite or delete it. Try again after restarting the app.",
  mutationAlertTitle: "Saved workouts unavailable",
  duplicate: "The workout library could not confirm the duplicate, so no new copy was shown.",
  reorder: "The workout library could not confirm the new order. Your previous order was kept.",
  tapeDuplicate: "The duplicate could not be verified. Your saved tapes were left unchanged.",
  tapeReorder: "The new order could not be verified. The previous order was kept.",
  tapeSave: "The workout library could not verify the save. Your tape remains open.",
  tapeUnavailable: "Saved tapes are unavailable. The open draft has not changed.",
  tapeEmpty:
    "Save this tape once and it will appear here for opening, duplication, and ordering.",
});

/** `{name}` is substituted with the plan name at the call site. */
export const PLAN_DELETE_FAILURE_TEMPLATE =
  "The workout library could not confirm that {name} was deleted. Your saved data was left in place.";

export const TAPE_DELETE_FAILURE_TEMPLATE =
  "The deletion could not be verified. {name} remains saved.";

export const COMBO_SOURCE_COPY: Readonly<
  Record<ComboSource, { readonly title: string; readonly detail: string }>
> = Object.freeze({
  generated: { title: "Generated", detail: "Fresh" },
  saved: { title: "Saved", detail: "Favorites" },
  mixed: { title: "Mix", detail: "Both" },
});

export const NO_SAVED_COMBOS_NOTICE =
  "No combos are saved yet, so the timer will generate calls until you add favorites in Build → Combo Lab.";

// ---------------------------------------------------------------------------
// Authored dosage limits
// ---------------------------------------------------------------------------

export interface DosageRange {
  readonly minimum: Milliseconds;
  readonly maximum: Milliseconds;
  readonly step: Milliseconds;
}

/**
 * There is no free text entry and no slider anywhere in the builder: ranges and
 * steps are the ONLY validation applied to authored durations. Two editors
 * write into the same plan with different ceilings, so the compact editor and
 * the tape inspector are separate authorities and the persistence check below
 * has to accept the union of both.
 */
export const INTERVAL_EDITOR_WORK_RANGE: DosageRange = Object.freeze({
  minimum: 15_000,
  maximum: 900_000,
  step: 15_000,
});

export const INTERVAL_EDITOR_REST_RANGE: DosageRange = Object.freeze({
  minimum: 0,
  maximum: 300_000,
  step: 15_000,
});

export const TAPE_INSPECTOR_WORK_RANGE: DosageRange = Object.freeze({
  minimum: 15_000,
  maximum: 3_600_000,
  step: 15_000,
});

export const TAPE_INSPECTOR_REST_RANGE: DosageRange = Object.freeze({
  minimum: 0,
  maximum: 900_000,
  step: 15_000,
});

/** What a persisted plan must satisfy: the widest range either editor can write. */
export const PERSISTED_WORK_RANGE: DosageRange = TAPE_INSPECTOR_WORK_RANGE;
export const PERSISTED_REST_RANGE: DosageRange = TAPE_INSPECTOR_REST_RANGE;

export const REPETITION_RANGE = Object.freeze({ minimum: 1, maximum: 200 });
export const SECTION_REPEAT_RANGE = Object.freeze({ minimum: 1, maximum: 20 });
export const QUICK_TIMER_ROUND_RANGE = Object.freeze({ minimum: 1, maximum: 20 });

export const QUICK_TIMER_WORK_RANGE: DosageRange = Object.freeze({
  minimum: 30_000,
  maximum: 600_000,
  step: 15_000,
});

export const QUICK_TIMER_REST_RANGE: DosageRange = Object.freeze({
  minimum: 0,
  maximum: 300_000,
  step: 15_000,
});

export const QUICK_TIMER_PREP_RANGE: DosageRange = Object.freeze({
  minimum: 0,
  maximum: 60_000,
  step: 5_000,
});

/** Offered cadences for changing the boxing call; 0 means manual only. */
export const COMBO_CADENCE_CHOICES: readonly Milliseconds[] = Object.freeze([
  0, 30_000, 60_000, 90_000, 120_000,
]);

export const ROUND_RHYTHM_WORK_CHOICES: readonly Milliseconds[] = Object.freeze([
  15_000, 20_000, 30_000, 45_000, 60_000, 90_000, 120_000,
]);

export const ROUND_RHYTHM_RESET_CHOICES: readonly Milliseconds[] = Object.freeze([
  5_000, 10_000, 15_000, 20_000, 30_000, 45_000, 60_000,
]);

/** Displayed values when the rhythm toggle is switched on with nothing stored. */
export const ROUND_RHYTHM_DEFAULT_WORK: Milliseconds = 45_000;
export const ROUND_RHYTHM_DEFAULT_RESET: Milliseconds = 15_000;

export const DEFAULT_COUNTDOWN_CUE: Milliseconds = 10_000;
export const DEFAULT_DRILL_SWITCH_INTERVAL: Milliseconds = 60_000;

/**
 * Clamps to a stepper's range without snapping to its step. The steppers move
 * in whole steps, but a seeded value from an older plan is only clamped, never
 * rounded — silently rewriting an authored duration would change a workout the
 * user already trained.
 */
export function clampToRange(value: Milliseconds, range: DosageRange): Milliseconds {
  return Math.min(range.maximum, Math.max(range.minimum, value));
}

export function decrementStepper(value: Milliseconds, range: DosageRange): Milliseconds {
  return Math.max(range.minimum, value - range.step);
}

export function incrementStepper(value: Milliseconds, range: DosageRange): Milliseconds {
  return Math.min(range.maximum, value + range.step);
}

// ---------------------------------------------------------------------------
// Drill library
// ---------------------------------------------------------------------------

export type DrillCategory =
  | "warmup"
  | "boxing"
  | "defense"
  | "conditioning"
  | "strength"
  | "recovery";

export const DRILL_CATEGORY_TITLE: Readonly<Record<DrillCategory, string>> = Object.freeze({
  warmup: "Warm up",
  boxing: "Boxing",
  defense: "Defense",
  conditioning: "Conditioning",
  strength: "Strength",
  recovery: "Recovery",
});

export const DRILL_CATEGORY_ORDER: readonly DrillCategory[] = Object.freeze([
  "warmup",
  "boxing",
  "defense",
  "conditioning",
  "strength",
  "recovery",
] as const);

/** The catalog is fixed at build time, so equipment is a closed set too. */
export type DrillEquipment =
  | "Jump rope"
  | "None"
  | "Bag optional"
  | "Slip line optional"
  | "Heavy bag"
  | "Mat optional";

/**
 * Field names a drill must never carry. The exercise library is informational:
 * category and equipment are annotations, and every prescription — duration,
 * reps, sets, rest, tempo, intensity — is authored in the interval editor. A
 * drill that could express dosage would let one screen contradict another about
 * how much work the user was told to do.
 */
export type ProhibitedDosageKey =
  | "duration"
  | "workDuration"
  | "restDuration"
  | "rest"
  | "reps"
  | "repetitions"
  | "targetRepetitions"
  | "sets"
  | "setCount"
  | "tempo"
  | "intensity"
  | "load"
  | "rpe"
  | "target";

/**
 * Structurally forbids dosage. Because each prohibited key is declared as
 * `never`, an object literal that supplies one fails to type-check — the rule
 * is enforced by the compiler rather than by review.
 */
export type DosageFree<T> = T & { readonly [K in ProhibitedDosageKey]?: never };

export type TrainingDrill = DosageFree<{
  readonly id: DrillId;
  readonly name: string;
  readonly category: DrillCategory;
  readonly summary: string;
  readonly coachingCue: string;
  readonly equipment: DrillEquipment;
}>;

function catalogDrill(
  id: string,
  name: string,
  category: DrillCategory,
  summary: string,
  coachingCue: string,
  equipment: DrillEquipment,
): TrainingDrill {
  return { id: drillId(id), name, category, summary, coachingCue, equipment };
}

/**
 * Twenty-eight fixed entries. No user CRUD, no import, no versioning — the ids
 * are stored on intervals as `sourceDrillId`, so they are persisted product
 * identifiers and must never be renamed.
 */
export const DRILL_CATALOG: readonly TrainingDrill[] = Object.freeze([
  catalogDrill(
    "rope-boxer-step",
    "Boxer-step jump rope",
    "warmup",
    "Alternate weight with a low, quiet bounce.",
    "Keep the rope close, shoulders relaxed, and land softly under your hips.",
    "Jump rope",
  ),
  catalogDrill(
    "rope-basic",
    "Basic jump rope",
    "warmup",
    "Easy rhythm to raise temperature and find your feet.",
    "Turn from the wrists. Keep jumps small enough for the rope to pass.",
    "Jump rope",
  ),
  catalogDrill(
    "mobility",
    "Shoulder + hip mobility",
    "warmup",
    "Controlled circles, reaches, and hip openers.",
    "Move through a comfortable range without forcing the end position.",
    "None",
  ),
  catalogDrill(
    "shadow-light",
    "Light shadowboxing",
    "warmup",
    "Easy punches, stance resets, and relaxed footwork.",
    "Exhale on each shot and return both hands to guard.",
    "None",
  ),
  catalogDrill(
    "straight-rhythm",
    "Straight-shot rhythm",
    "boxing",
    "Jab and cross combinations with a clean reset.",
    "Turn the rear hip on the cross; recover to stance before repeating.",
    "Bag optional",
  ),
  catalogDrill(
    "hook-entry",
    "Lead-hook entry",
    "boxing",
    "Set up the lead hook behind straight punches.",
    "Rotate around a stable base and keep the elbow aligned with the fist.",
    "Bag optional",
  ),
  catalogDrill(
    "uppercut-series",
    "Uppercut series",
    "boxing",
    "Short inside combinations using punches five and six.",
    "Bend through the knees, not the waist, and keep the motion compact.",
    "Bag optional",
  ),
  catalogDrill(
    "shadow-free",
    "Free shadowboxing",
    "boxing",
    "Blend offense, defense, angles, and resets.",
    "Picture an opponent and finish every exchange in a balanced stance.",
    "None",
  ),
  catalogDrill(
    "slip-reps",
    "Slip reps",
    "defense",
    "Repeat small lead- and rear-side slips.",
    "Move just outside the center line; eyes stay forward and knees stay soft.",
    "Slip line optional",
  ),
  catalogDrill(
    "roll-reps",
    "Roll reps",
    "defense",
    "Trace a compact U under an imagined hook.",
    "Keep the chest proud and let the legs create the level change.",
    "Slip line optional",
  ),
  catalogDrill(
    "slip-counter",
    "Slip + counter",
    "defense",
    "Pair one defensive move with a straight counter.",
    "Return from the slip through your stance before firing the counter.",
    "Bag optional",
  ),
  catalogDrill(
    "roll-hook",
    "Roll + hook exit",
    "defense",
    "Roll under, hook, then step off line.",
    "Stay compact through the roll and leave on a stable angle.",
    "Bag optional",
  ),
  catalogDrill(
    "catch-return",
    "Catch + return",
    "defense",
    "Catch an imagined lead straight and answer with a rear straight.",
    "Move the rear glove only far enough to meet the jab, keep the elbow tucked, then return to guard after the counter.",
    "Bag optional",
  ),
  catalogDrill(
    "hook-block-return",
    "Hook block + return",
    "defense",
    "Shield an imagined head hook with the appropriate forearm, then answer in stance.",
    "Keep the block compact and the other hand home. Rebuild your guard before repeating.",
    "None",
  ),
  catalogDrill(
    "body-block-reps",
    "Body block reps",
    "defense",
    "Use compact lead- and rear-side elbow or forearm blocks against imagined body punches.",
    "Lower the elbow without folding at the waist; keep your eyes up and return directly to guard.",
    "None",
  ),
  catalogDrill(
    "mixed-block-counter",
    "Mixed block + counter",
    "defense",
    "Read a called threat, choose the matching catch or block, then counter.",
    "Make the smallest effective defense, stay balanced, and let the defended side determine your return.",
    "Bag optional",
  ),
  catalogDrill(
    "speed-push",
    "Speed push",
    "conditioning",
    "Fast, technically clean punches at controlled force.",
    "Relax the hands between shots and stop speed from pulling you off balance.",
    "Bag optional",
  ),
  catalogDrill(
    "power-accents",
    "Power accents",
    "conditioning",
    "Use lighter setup shots before one deliberate hard shot.",
    "Power comes from the floor and hips; never sacrifice alignment for force.",
    "Heavy bag",
  ),
  catalogDrill(
    "punch-out",
    "Straight punch-out",
    "conditioning",
    "Sustained straight punches with a steady base.",
    "Choose a pace you can hold and keep the shoulders away from the ears.",
    "Bag optional",
  ),
  catalogDrill(
    "high-knees",
    "High knees",
    "conditioning",
    "Quick feet during an active-recovery window.",
    "Stay tall, land softly, and reduce range before form breaks.",
    "None",
  ),
  catalogDrill(
    "plank",
    "Forearm plank",
    "strength",
    "Brace through the trunk while breathing steadily.",
    "Keep ribs stacked over pelvis; stop if the low back begins to sag.",
    "Mat optional",
  ),
  catalogDrill(
    "dead-bug",
    "Dead bug",
    "strength",
    "Alternate limbs while maintaining trunk position.",
    "Use a range that lets the low back stay gently connected to the floor.",
    "Mat optional",
  ),
  catalogDrill(
    "bicycle",
    "Bicycle crunch",
    "strength",
    "Slow alternating trunk rotation.",
    "Lead with the rib cage rather than pulling the head with the hands.",
    "Mat optional",
  ),
  catalogDrill(
    "pushup-tap",
    "Push-up + shoulder tap",
    "strength",
    "Press, stabilize, and alternate shoulder taps.",
    "Widen the feet as needed to keep the hips quiet.",
    "Mat optional",
  ),
  catalogDrill(
    "boxer-bounce",
    "Boxer bounce",
    "recovery",
    "Stay light on the feet while breathing down.",
    "Shake out the arms and make the bounce smaller as you recover.",
    "None",
  ),
  catalogDrill(
    "walk-breathe",
    "Walk + breathe",
    "recovery",
    "Easy movement with a longer exhale.",
    "Let breathing settle gradually; do not stop abruptly after hard work.",
    "None",
  ),
  catalogDrill(
    "shoulder-release",
    "Shoulder release",
    "recovery",
    "Gentle shoulder, chest, and upper-back mobility.",
    "Keep the stretch mild and breathe without forcing range.",
    "None",
  ),
  catalogDrill(
    "box-breath",
    "Box breathing",
    "recovery",
    "Even inhale, hold, exhale, and hold phases.",
    "Use a comfortable count and return to normal breathing if lightheaded.",
    "None",
  ),
]);

export function findDrill(id: DrillId): TrainingDrill | undefined {
  return DRILL_CATALOG.find((drill) => drill.id === id);
}

export function drillsInCategory(category: DrillCategory): readonly TrainingDrill[] {
  return DRILL_CATALOG.filter((drill) => drill.category === category);
}

/**
 * The library row spells the empty case out; the detail sheet's labelled
 * "Equipment" field does not, because the label already supplies the context.
 */
export function drillEquipmentRowLabel(equipment: DrillEquipment): string {
  return equipment === "None" ? "No equipment" : equipment;
}

export const DRILL_LIBRARY_COPY = Object.freeze({
  browseTitle: "Exercise library",
  browseEyebrow: "Exercise library",
  browseHeadline: "Explore the work",
  browseBody:
    "Movement ideas and coaching cues for warm-up, bag work, defense, strength, and recovery. Set timing or repetitions when you build a workout.",
  browseAction: "Done",
  pickerTitle: "Add activity",
  pickerEyebrow: "Workout activity",
  pickerHeadline: "Choose an activity",
  pickerBody:
    "Choose the movement here. You'll set its timing, repetitions, and recovery in the interval editor.",
  pickerAction: "Set interval",
  allCategories: "All",
  equipmentLabel: "Equipment",
  coachingCueLabel: "Coaching cue",
});

// ---------------------------------------------------------------------------
// Resolution rules
// ---------------------------------------------------------------------------

export function resolvedRepeatCount(section: WorkoutSection): number {
  return Math.max(SECTION_REPEAT_RANGE.minimum, Math.trunc(section.repeatCount));
}

export function resolvedIntervalKind(interval: WorkoutInterval): SegmentKind {
  return interval.kind ?? "work";
}

/**
 * An explicit role always wins. Otherwise a catalog drill makes the interval a
 * standalone drill — that is what keeps "Forearm plank" from being announced as
 * boxing round four — and only an unannotated work interval becomes a round.
 */
export function resolvedIntervalRole(interval: WorkoutInterval): SegmentRole {
  if (interval.role !== undefined) return interval.role;
  if (interval.sourceDrillId !== undefined) return "standaloneDrill";
  return resolvedIntervalKind(interval) === "work" ? "boxingRound" : "phase";
}

export function isBoxingRoundInterval(interval: WorkoutInterval): boolean {
  return resolvedIntervalKind(interval) === "work" && resolvedIntervalRole(interval) === "boxingRound";
}

export function isBoxingRoundSegment(segment: SessionSegment): boolean {
  return segment.kind === "work" && segment.role === "boxingRound";
}

export function resolvedComboSource(plan: WorkoutPlan): ComboSource {
  return plan.comboSource ?? DEFAULT_COMBO_SOURCE;
}

/** Blank names are a normal authoring state, not an error; each mode has a fallback. */
export function resolvedPlanName(plan: WorkoutPlan): string {
  const trimmed = plan.name.trim();
  return trimmed.length > 0 ? trimmed : PLAN_NAME_FALLBACK[plan.mode];
}

export function resolvedSectionName(section: WorkoutSection): string {
  const trimmed = section.name.trim();
  return trimmed.length > 0 ? trimmed : SECTION_NAME_FALLBACK;
}

export function restDuration(rest: IntervalRest): Milliseconds {
  return rest.kind === "recovery" ? Math.max(0, rest.duration) : 0;
}

// ---------------------------------------------------------------------------
// Derived segment identity
// ---------------------------------------------------------------------------

const FNV_PRIME = 1_099_511_628_211n;
const FNV_MASK = 0xffff_ffff_ffff_ffffn;
const FNV_OFFSET_FIRST = 14_695_981_039_346_656_037n;
const FNV_OFFSET_SECOND = 7_809_847_782_469_553_979n;

/**
 * UTF-8 encoding by hand rather than TextEncoder: the domain package must not
 * touch platform globals, and the hash below has to produce byte-for-byte the
 * same input the Swift app hashes.
 */
function utf8Bytes(input: string): number[] {
  const bytes: number[] = [];
  for (const character of input) {
    const point = character.codePointAt(0) ?? 0;
    if (point < 0x80) {
      bytes.push(point);
    } else if (point < 0x800) {
      bytes.push(0xc0 | (point >> 6), 0x80 | (point & 0x3f));
    } else if (point < 0x10000) {
      bytes.push(0xe0 | (point >> 12), 0x80 | ((point >> 6) & 0x3f), 0x80 | (point & 0x3f));
    } else {
      bytes.push(
        0xf0 | (point >> 18),
        0x80 | ((point >> 12) & 0x3f),
        0x80 | ((point >> 6) & 0x3f),
        0x80 | (point & 0x3f),
      );
    }
  }
  return bytes;
}

/**
 * Repeated occurrences need stable ids that are not the interval's own id —
 * otherwise round three and round seven of the same authored interval would
 * collide in the engine, in restore, and in every accessibility identifier.
 * Two 64-bit FNV-1 variants are concatenated and stamped with the version and
 * variant nibbles so the result is a well-formed v5-shaped UUID; it is derived,
 * not random, so a restored session resolves to exactly the same segments.
 *
 * The input mirrors the Swift interpolation exactly, including uppercase UUID
 * spelling, because these ids are compared across platforms.
 */
export function derivedOccurrenceId(
  baseId: WorkoutIntervalId,
  sectionId: WorkoutSectionId,
  repeatIndex: number,
  intervalIndex: number,
  discriminator: "work" | "rest",
): SessionSegmentId {
  const input = `${baseId.toUpperCase()}|${sectionId.toUpperCase()}|${repeatIndex}|${intervalIndex}|${discriminator}`;
  let first = FNV_OFFSET_FIRST;
  let second = FNV_OFFSET_SECOND;
  for (const byte of utf8Bytes(input)) {
    const value = BigInt(byte);
    first = ((first ^ value) * FNV_PRIME) & FNV_MASK;
    second = (((second * FNV_PRIME) & FNV_MASK) ^ value) & FNV_MASK;
  }

  const digits = (
    first.toString(16).padStart(16, "0") + second.toString(16).padStart(16, "0")
  ).split("");
  digits[12] = "5";
  const variantNibble = Number.parseInt(digits[16] ?? "0", 16);
  digits[16] = ((variantNibble & 0x3) | 0x8).toString(16);
  const hex = digits.join("");

  const formatted = [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join("-");
  return sessionSegmentId(formatted.toUpperCase());
}

// ---------------------------------------------------------------------------
// Flattening
// ---------------------------------------------------------------------------

interface Occurrence {
  readonly sectionId: WorkoutSectionId;
  readonly sectionIndex: number;
  readonly repeatIndex: number;
  readonly intervalIndex: number;
  readonly interval: WorkoutInterval;
}

function occurrences(sections: readonly WorkoutSection[]): Occurrence[] {
  const result: Occurrence[] = [];
  for (const [sectionIndex, section] of sections.entries()) {
    const repeats = resolvedRepeatCount(section);
    for (let repeatIndex = 0; repeatIndex < repeats; repeatIndex += 1) {
      for (const [intervalIndex, interval] of section.intervals.entries()) {
        result.push({
          sectionId: section.id,
          sectionIndex,
          repeatIndex,
          intervalIndex,
          interval,
        });
      }
    }
  }
  return result;
}

/**
 * Walks sections in order, expands repeats, and emits one work segment per
 * occurrence plus a synthesized recovery segment when the interval has rest AND
 * another activity follows. The trailing rest of the final occurrence is
 * dropped so an eight-round timer ends on work rather than on an empty minute.
 *
 * The very first occurrence of the first section reuses the authored interval
 * id, which is what lets an unrepeated plan's segment ids match the ids the
 * builder shows.
 */
export function flattenWorkoutPlan(plan: WorkoutPlan): readonly SessionSegment[] {
  const all = occurrences(plan.sections);
  const segments: SessionSegment[] = [];
  let boxingRound = 0;

  for (const [index, occurrence] of all.entries()) {
    const { interval } = occurrence;
    const kind = resolvedIntervalKind(interval);
    const role = resolvedIntervalRole(interval);
    const isRound = kind === "work" && role === "boxingRound";
    if (isRound) boxingRound += 1;

    const isFirstOccurrence = occurrence.sectionIndex === 0 && occurrence.repeatIndex === 0;
    const workId = isFirstOccurrence
      ? sessionSegmentId(interval.id)
      : derivedOccurrenceId(
          interval.id,
          occurrence.sectionId,
          occurrence.repeatIndex,
          occurrence.intervalIndex,
          "work",
        );

    const work: SessionSegment = {
      id: workId,
      kind,
      title: interval.title,
      detail: interval.detail,
      // Negative authored durations cannot survive into the runtime clock.
      duration: Math.max(0, interval.workDuration),
      ...(isRound ? { roundNumber: boxingRound } : {}),
      ...(interval.drill !== undefined ? { drill: interval.drill } : {}),
      ...(interval.comboTheme !== undefined ? { comboTheme: interval.comboTheme } : {}),
      target: interval.target,
      ...(interval.sourceDrillId !== undefined ? { sourceDrillId: interval.sourceDrillId } : {}),
      role,
      sectionId: occurrence.sectionId,
      intervalId: interval.id,
    };
    segments.push(work);

    const hasFollowingActivity = index < all.length - 1;
    const rest = interval.rest;
    if (hasFollowingActivity && rest.kind === "recovery" && rest.duration > 0) {
      segments.push({
        id: derivedOccurrenceId(
          interval.id,
          occurrence.sectionId,
          occurrence.repeatIndex,
          occurrence.intervalIndex,
          "rest",
        ),
        kind: "recovery",
        title: rest.title ?? REST_SEGMENT_FALLBACK.title,
        detail: rest.detail ?? REST_SEGMENT_FALLBACK.detail,
        duration: rest.duration,
        ...(rest.drill !== undefined ? { drill: rest.drill } : {}),
        target: TIMED_TARGET,
        role: "phase",
        sectionId: occurrence.sectionId,
        intervalId: interval.id,
      });
    }
  }

  return segments;
}

/** Σ flattened segment durations — repeats included, final rest excluded. */
export function totalPlannedDuration(plan: WorkoutPlan): Milliseconds {
  return sumSegmentDurations(flattenWorkoutPlan(plan));
}

export function sumSegmentDurations(segments: readonly SessionSegment[]): Milliseconds {
  return segments.reduce((total, segment) => total + Math.max(0, segment.duration), 0);
}

/** Count of flattened boxing rounds; this is the number the round pill shows. */
export function countWorkRounds(plan: WorkoutPlan): number {
  return flattenWorkoutPlan(plan).filter(isBoxingRoundSegment).length;
}

/**
 * The RAW sum of authored intervals. The builder summary deliberately does not
 * multiply by repeat count: the user authored this many activities, and showing
 * the expanded number would make one repeated section look like a longer list
 * than it is.
 */
export function countAuthoredIntervals(plan: WorkoutPlan): number {
  return plan.sections.reduce((total, section) => total + section.intervals.length, 0);
}

/**
 * A section's own share of the plan, used for the tape lane's proportional
 * width. Unlike the flattened total this keeps every trailing rest, because a
 * lane segment represents the authored block rather than the executed one.
 */
export function sectionDuration(section: WorkoutSection): Milliseconds {
  const perPass = section.intervals.reduce(
    (total, interval) => total + Math.max(0, interval.workDuration) + restDuration(interval.rest),
    0,
  );
  return perPass * resolvedRepeatCount(section);
}

/** The immutable snapshot the engine runs. */
export function deriveSessionPlan(plan: WorkoutPlan): SessionPlan {
  const segments = flattenWorkoutPlan(plan);
  return {
    planId: plan.id,
    name: resolvedPlanName(plan),
    mode: plan.mode,
    segments,
    totalDuration: sumSegmentDurations(segments),
    workRounds: segments.filter(isBoxingRoundSegment).length,
    halfwayCue: plan.halfwayCue,
    countdownCue: Math.max(0, plan.countdownCue),
    drillSwitchInterval: Math.max(0, plan.drillSwitchInterval),
    workBurst: Math.max(0, plan.workBurst),
    burstRest: Math.max(0, plan.burstRest),
    comboSource: resolvedComboSource(plan),
  };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Every issue names the exact object it is about so the builder can move focus
 * to the offending field. A plan that produces no issues is one the store is
 * allowed to write and the start bar is allowed to enable.
 */
export type WorkoutValidationIssue =
  | { readonly kind: "planHasNoSections" }
  | { readonly kind: "planHasNoSegments" }
  | { readonly kind: "planDurationNotPositive"; readonly totalDuration: Milliseconds }
  | { readonly kind: "duplicateSectionId"; readonly sectionId: WorkoutSectionId }
  | { readonly kind: "duplicateIntervalId"; readonly intervalId: WorkoutIntervalId }
  | {
      readonly kind: "repeatCountOutOfRange";
      readonly sectionId: WorkoutSectionId;
      readonly repeatCount: number;
    }
  | {
      readonly kind: "intervalTitleEmpty";
      readonly sectionId: WorkoutSectionId;
      readonly intervalId: WorkoutIntervalId;
    }
  | {
      readonly kind: "workDurationOutOfRange";
      readonly sectionId: WorkoutSectionId;
      readonly intervalId: WorkoutIntervalId;
      readonly duration: Milliseconds;
      readonly range: DosageRange;
    }
  | {
      readonly kind: "restDurationOutOfRange";
      readonly sectionId: WorkoutSectionId;
      readonly intervalId: WorkoutIntervalId;
      readonly duration: Milliseconds;
      readonly range: DosageRange;
    }
  | {
      readonly kind: "repetitionsOutOfRange";
      readonly sectionId: WorkoutSectionId;
      readonly intervalId: WorkoutIntervalId;
      readonly repetitions: number;
    };

export interface WorkoutValidation {
  readonly issues: readonly WorkoutValidationIssue[];
  readonly isValid: boolean;
  /** The start bar's own guard, re-checked at request time. */
  readonly isStartable: boolean;
  readonly totalDuration: Milliseconds;
}

export interface WorkoutValidationBounds {
  readonly work: DosageRange;
  readonly rest: DosageRange;
  /** Countdown intervals have their own, much lower, authored floor. */
  readonly preparation: DosageRange;
}

export const PERSISTED_VALIDATION_BOUNDS: WorkoutValidationBounds = Object.freeze({
  work: PERSISTED_WORK_RANGE,
  rest: PERSISTED_REST_RANGE,
  preparation: QUICK_TIMER_PREP_RANGE,
});

function isWholeNumber(value: number): boolean {
  return Number.isFinite(value) && Math.trunc(value) === value;
}

/**
 * Collections require unique ids and a non-empty flattened segment list: a plan
 * with duplicate ids cannot be addressed unambiguously at runtime, and a plan
 * with no segments would archive as a session that never happened.
 *
 * `bounds` is a parameter because the compact editor and the tape inspector are
 * separate authorities over the same fields; persistence checks the union.
 */
export function validateWorkoutPlan(
  plan: WorkoutPlan,
  bounds: WorkoutValidationBounds = PERSISTED_VALIDATION_BOUNDS,
): WorkoutValidation {
  const issues: WorkoutValidationIssue[] = [];
  const seenSections = new Set<WorkoutSectionId>();
  const seenIntervals = new Set<WorkoutIntervalId>();

  if (plan.sections.length === 0) {
    issues.push({ kind: "planHasNoSections" });
  }

  for (const section of plan.sections) {
    if (seenSections.has(section.id)) {
      issues.push({ kind: "duplicateSectionId", sectionId: section.id });
    }
    seenSections.add(section.id);

    if (
      !isWholeNumber(section.repeatCount) ||
      section.repeatCount < SECTION_REPEAT_RANGE.minimum ||
      section.repeatCount > SECTION_REPEAT_RANGE.maximum
    ) {
      issues.push({
        kind: "repeatCountOutOfRange",
        sectionId: section.id,
        repeatCount: section.repeatCount,
      });
    }

    for (const interval of section.intervals) {
      if (seenIntervals.has(interval.id)) {
        issues.push({ kind: "duplicateIntervalId", intervalId: interval.id });
      }
      seenIntervals.add(interval.id);

      if (interval.title.trim().length === 0) {
        issues.push({
          kind: "intervalTitleEmpty",
          sectionId: section.id,
          intervalId: interval.id,
        });
      }

      // A "Get ready" interval is authored by the quick timer's prep stepper,
      // not by the interval editor's work stepper, so it is legitimately
      // shorter than any work interval is allowed to be. Validating it against
      // the work range would reject the app's own default 10 s countdown.
      const workRange =
        resolvedIntervalKind(interval) === "preparation" ? bounds.preparation : bounds.work;
      if (interval.workDuration < workRange.minimum || interval.workDuration > workRange.maximum) {
        issues.push({
          kind: "workDurationOutOfRange",
          sectionId: section.id,
          intervalId: interval.id,
          duration: interval.workDuration,
          range: workRange,
        });
      }

      const rest = restDuration(interval.rest);
      if (rest < bounds.rest.minimum || rest > bounds.rest.maximum) {
        issues.push({
          kind: "restDurationOutOfRange",
          sectionId: section.id,
          intervalId: interval.id,
          duration: rest,
          range: bounds.rest,
        });
      }

      if (interval.target.mode === "repetitions") {
        const { repetitions } = interval.target;
        if (
          !isWholeNumber(repetitions) ||
          repetitions < REPETITION_RANGE.minimum ||
          repetitions > REPETITION_RANGE.maximum
        ) {
          issues.push({
            kind: "repetitionsOutOfRange",
            sectionId: section.id,
            intervalId: interval.id,
            repetitions,
          });
        }
      }
    }
  }

  const segments = flattenWorkoutPlan(plan);
  const totalDuration = sumSegmentDurations(segments);
  if (segments.length === 0) {
    issues.push({ kind: "planHasNoSegments" });
  }
  if (totalDuration <= 0) {
    issues.push({ kind: "planDurationNotPositive", totalDuration });
  }

  return {
    issues,
    isValid: issues.length === 0,
    isStartable: segments.length > 0 && totalDuration > 0,
    totalDuration,
  };
}

// ---------------------------------------------------------------------------
// Section and interval editing
// ---------------------------------------------------------------------------

function asIntervalList(intervals: readonly WorkoutInterval[]): WorkoutIntervalList | undefined {
  const [first, ...rest] = intervals;
  return first === undefined ? undefined : [first, ...rest];
}

function swapAdjacent<T>(items: readonly T[], index: number, offset: -1 | 1): readonly T[] {
  const target = index + offset;
  const moved = items[index];
  const displaced = items[target];
  if (moved === undefined || displaced === undefined) return items;
  const next = items.slice();
  next[index] = displaced;
  next[target] = moved;
  return next;
}

function removeAndInsertBefore<T>(
  items: readonly T[],
  fromIndex: number,
  targetIndex: number,
): readonly T[] {
  const moved = items[fromIndex];
  const target = items[targetIndex];
  if (moved === undefined || target === undefined || fromIndex === targetIndex) return items;
  const without = items.filter((_, index) => index !== fromIndex);
  const insertion = without.indexOf(target);
  if (insertion < 0) return items;
  return [...without.slice(0, insertion), moved, ...without.slice(insertion)];
}

/**
 * Menu/keyboard reordering SWAPS adjacent items. Drag-and-drop REMOVES and
 * INSERTS BEFORE the target, which shifts the target down by one when moving
 * forward. These produce different results for non-adjacent moves and both are
 * product behavior, so they are two functions rather than one with a flag.
 */
export function moveIntervalEarlier(
  section: WorkoutSection,
  intervalId: WorkoutIntervalId,
): WorkoutSection {
  const index = section.intervals.findIndex((interval) => interval.id === intervalId);
  if (index <= 0) return section;
  const moved = asIntervalList(swapAdjacent(section.intervals, index, -1));
  return moved === undefined ? section : { ...section, intervals: moved };
}

export function moveIntervalLater(
  section: WorkoutSection,
  intervalId: WorkoutIntervalId,
): WorkoutSection {
  const index = section.intervals.findIndex((interval) => interval.id === intervalId);
  if (index < 0 || index >= section.intervals.length - 1) return section;
  const moved = asIntervalList(swapAdjacent(section.intervals, index, 1));
  return moved === undefined ? section : { ...section, intervals: moved };
}

export function moveIntervalBefore(
  section: WorkoutSection,
  movedId: WorkoutIntervalId,
  targetId: WorkoutIntervalId,
): WorkoutSection {
  const fromIndex = section.intervals.findIndex((interval) => interval.id === movedId);
  const targetIndex = section.intervals.findIndex((interval) => interval.id === targetId);
  if (fromIndex < 0 || targetIndex < 0) return section;
  const moved = asIntervalList(removeAndInsertBefore(section.intervals, fromIndex, targetIndex));
  return moved === undefined ? section : { ...section, intervals: moved };
}

export function moveSectionEarlier(plan: WorkoutPlan, sectionId: WorkoutSectionId): WorkoutPlan {
  const index = plan.sections.findIndex((section) => section.id === sectionId);
  if (index <= 0) return plan;
  return { ...plan, sections: swapAdjacent(plan.sections, index, -1) };
}

export function moveSectionLater(plan: WorkoutPlan, sectionId: WorkoutSectionId): WorkoutPlan {
  const index = plan.sections.findIndex((section) => section.id === sectionId);
  if (index < 0 || index >= plan.sections.length - 1) return plan;
  return { ...plan, sections: swapAdjacent(plan.sections, index, 1) };
}

export function moveSectionBefore(
  plan: WorkoutPlan,
  movedId: WorkoutSectionId,
  targetId: WorkoutSectionId,
): WorkoutPlan {
  const fromIndex = plan.sections.findIndex((section) => section.id === movedId);
  const targetIndex = plan.sections.findIndex((section) => section.id === targetId);
  if (fromIndex < 0 || targetIndex < 0) return plan;
  return { ...plan, sections: removeAndInsertBefore(plan.sections, fromIndex, targetIndex) };
}

/**
 * Duplicates in place, immediately after the source, keeping every field —
 * including the rest metadata, which is easy to lose because it is nested.
 */
export function duplicateInterval(
  section: WorkoutSection,
  intervalId: WorkoutIntervalId,
  newId: WorkoutIntervalId,
): WorkoutSection {
  const index = section.intervals.findIndex((interval) => interval.id === intervalId);
  const source = section.intervals[index];
  if (source === undefined) return section;
  const copy: WorkoutInterval = { ...source, id: newId };
  const next = asIntervalList([
    ...section.intervals.slice(0, index + 1),
    copy,
    ...section.intervals.slice(index + 1),
  ]);
  return next === undefined ? section : { ...section, intervals: next };
}

/**
 * Returns `undefined` when the removed interval was the last one: a section
 * cannot exist without intervals, so removing the final interval removes the
 * section. Making that the return type forces every caller to decide what
 * happens to the section instead of leaving an empty one behind.
 */
export function removeInterval(
  section: WorkoutSection,
  intervalId: WorkoutIntervalId,
): WorkoutSection | undefined {
  const remaining = section.intervals.filter((interval) => interval.id !== intervalId);
  if (remaining.length === section.intervals.length) return section;
  const next = asIntervalList(remaining);
  return next === undefined ? undefined : { ...section, intervals: next };
}

export function appendInterval(
  section: WorkoutSection,
  interval: WorkoutInterval,
): WorkoutSection {
  return { ...section, intervals: [...section.intervals, interval] };
}

/**
 * Replaces by id and silently drops an unmatched id. The section editor works
 * on a local copy that can be committed after its section was deleted
 * elsewhere; resurrecting it would be worse than dropping the edit.
 */
export function replaceSection(plan: WorkoutPlan, section: WorkoutSection): WorkoutPlan {
  if (!plan.sections.some((existing) => existing.id === section.id)) return plan;
  return {
    ...plan,
    sections: plan.sections.map((existing) => (existing.id === section.id ? section : existing)),
  };
}

export function removeSection(plan: WorkoutPlan, sectionId: WorkoutSectionId): WorkoutPlan {
  return { ...plan, sections: plan.sections.filter((section) => section.id !== sectionId) };
}

export function replaceInterval(plan: WorkoutPlan, interval: WorkoutInterval): WorkoutPlan {
  return {
    ...plan,
    sections: plan.sections.map((section) => {
      if (!section.intervals.some((existing) => existing.id === interval.id)) return section;
      const next = asIntervalList(
        section.intervals.map((existing) => (existing.id === interval.id ? interval : existing)),
      );
      return next === undefined ? section : { ...section, intervals: next };
    }),
  };
}

/** Switching to Reps seeds 20; the work duration becomes a visible time cap. */
export function withRepetitionTarget(
  interval: WorkoutInterval,
  repetitions: number = SEEDED_REPETITION_TARGET,
): WorkoutInterval {
  return { ...interval, target: repetitionTarget(repetitions) };
}

/** Switching back to Timed erases the rep target entirely. */
export function withTimedTarget(interval: WorkoutInterval): WorkoutInterval {
  return { ...interval, target: TIMED_TARGET };
}

/**
 * Changing the kind rewrites the role, because a kind the user picked by hand
 * should not leave behind a role that contradicts it.
 */
export function withSegmentKind(interval: WorkoutInterval, kind: SegmentKind): WorkoutInterval {
  return { ...interval, kind, role: kind === "work" ? "boxingRound" : "phase" };
}

// ---------------------------------------------------------------------------
// Adding an activity from the library
// ---------------------------------------------------------------------------

interface DrillPlacement {
  readonly kind: SegmentKind;
  readonly role: SegmentRole;
  readonly comboTheme?: GuidedComboTheme;
}

/**
 * Only the boxing category produces a round with a call; every other category
 * becomes a standalone drill, so the live screen never announces combinations
 * over a plank.
 */
const DRILL_PLACEMENT: Readonly<Record<DrillCategory, DrillPlacement>> = {
  warmup: { kind: "warmup", role: "standaloneDrill" },
  boxing: { kind: "work", role: "boxingRound", comboTheme: DEFAULT_GUIDED_COMBO_THEME },
  defense: { kind: "work", role: "standaloneDrill" },
  conditioning: { kind: "work", role: "standaloneDrill" },
  strength: { kind: "strength", role: "standaloneDrill" },
  recovery: { kind: "recovery", role: "standaloneDrill" },
};

export function drillPlacement(category: DrillCategory): DrillPlacement {
  return DRILL_PLACEMENT[category];
}

/**
 * Stages an interval from a catalog drill. Timing is inherited from the
 * section's LAST existing interval, or from the standard open interval when the
 * section is empty — never from the drill, which structurally cannot carry a
 * duration or a rep count. The staged interval is then opened in the interval
 * editor so dosage is always chosen explicitly.
 */
export function intervalFromDrill(
  drill: TrainingDrill,
  id: WorkoutIntervalId,
  inheritFrom?: WorkoutInterval,
): WorkoutInterval {
  const template = inheritFrom ?? standardOpenInterval(id);
  const placement = drillPlacement(drill.category);
  return {
    id,
    title: drill.name,
    detail: drill.coachingCue,
    workDuration: template.workDuration,
    rest: template.rest,
    target: template.target,
    drill: drill.name,
    sourceDrillId: drill.id,
    role: placement.role,
    kind: placement.kind,
    ...(placement.comboTheme !== undefined ? { comboTheme: placement.comboTheme } : {}),
  };
}

/** The last interval is the inheritance source; an empty section has none. */
export function inheritanceSource(section: WorkoutSection): WorkoutInterval {
  const last = section.intervals[section.intervals.length - 1];
  return last ?? section.intervals[0];
}

// ---------------------------------------------------------------------------
// Presets, templates, and blueprints
// ---------------------------------------------------------------------------

/**
 * Templates are authored without ids so the same static definition can be
 * materialized repeatedly. The template instance is never edited: "Use" always
 * duplicates it, which is why nothing here mutates.
 */
export interface WorkoutIntervalBlueprint {
  readonly title: string;
  readonly detail: string;
  readonly workDuration: Milliseconds;
  readonly rest: IntervalRest;
  readonly target: IntervalTarget;
  readonly drill?: string;
  readonly comboTheme?: GuidedComboTheme;
  readonly sourceDrillId?: DrillId;
  readonly role?: SegmentRole;
  readonly kind?: SegmentKind;
}

export interface WorkoutSectionBlueprint {
  readonly name: string;
  readonly repeatCount: number;
  readonly intervals: readonly [WorkoutIntervalBlueprint, ...WorkoutIntervalBlueprint[]];
}

export interface WorkoutPlanBlueprint {
  readonly name: string;
  readonly mode: WorkoutMode;
  readonly sections: readonly WorkoutSectionBlueprint[];
  readonly halfwayCue: boolean;
  readonly countdownCue: Milliseconds;
  readonly drillSwitchInterval: Milliseconds;
  readonly workBurst: Milliseconds;
  readonly burstRest: Milliseconds;
  readonly comboSource?: ComboSource;
}

/**
 * Identity is injected rather than generated here so this package stays pure
 * and so tests can materialize a template deterministically.
 */
export interface WorkoutIdMinter {
  readonly plan: () => WorkoutPlanId;
  readonly section: () => WorkoutSectionId;
  readonly interval: () => WorkoutIntervalId;
}

export function materializeWorkoutPlan(
  blueprint: WorkoutPlanBlueprint,
  mint: WorkoutIdMinter,
): WorkoutPlan {
  return {
    id: mint.plan(),
    name: blueprint.name,
    mode: blueprint.mode,
    sections: blueprint.sections.map((section) => {
      const [first, ...rest] = section.intervals;
      const intervals: WorkoutIntervalList = [
        { ...first, id: mint.interval() },
        ...rest.map((interval) => ({ ...interval, id: mint.interval() })),
      ];
      return { id: mint.section(), name: section.name, repeatCount: section.repeatCount, intervals };
    }),
    halfwayCue: blueprint.halfwayCue,
    countdownCue: blueprint.countdownCue,
    drillSwitchInterval: blueprint.drillSwitchInterval,
    workBurst: blueprint.workBurst,
    burstRest: blueprint.burstRest,
    ...(blueprint.comboSource !== undefined ? { comboSource: blueprint.comboSource } : {}),
  };
}

/**
 * A duplicate is a genuinely new plan: new plan id, new section ids, new
 * interval ids. Sharing ids would make the copy and the original collide in the
 * saved library and in derived segment identity.
 */
export function duplicateWorkoutPlan(
  plan: WorkoutPlan,
  name: string,
  mint: WorkoutIdMinter,
): WorkoutPlan {
  return {
    ...plan,
    id: mint.plan(),
    name,
    sections: plan.sections.map((section) => {
      const [first, ...rest] = section.intervals;
      const intervals: WorkoutIntervalList = [
        { ...first, id: mint.interval() },
        ...rest.map((interval) => ({ ...interval, id: mint.interval() })),
      ];
      return { ...section, id: mint.section(), intervals };
    }),
  };
}

/**
 * The starter open interval. Recomputed per call because every access must
 * yield a fresh identity — reusing one instance would give two intervals the
 * same id and silently merge them at runtime.
 */
export function standardOpenInterval(id: WorkoutIntervalId): WorkoutInterval {
  return {
    id,
    title: "Open boxing",
    detail: "Your pace · your combinations",
    workDuration: 180_000,
    rest: {
      kind: "recovery",
      duration: 60_000,
      title: "Recover",
      detail: "Breathe and reset",
      drill: "Walk + breathe",
    },
    target: TIMED_TARGET,
    drill: "Open boxing",
    comboTheme: DEFAULT_GUIDED_COMBO_THEME,
    role: "boxingRound",
    kind: "work",
  };
}

export const NEW_GUIDED_PLAN_NAME = "My workout";
export const FIRST_SECTION_NAME = "Main work";
export const ADDITIONAL_SECTION_NAME = "New section";

/** A new guided workout: one section repeated three times, mixed call source. */
export function newGuidedWorkoutPlan(mint: WorkoutIdMinter): WorkoutPlan {
  const intervalId = mint.interval();
  return {
    id: mint.plan(),
    name: NEW_GUIDED_PLAN_NAME,
    mode: "guided",
    sections: [
      {
        id: mint.section(),
        name: FIRST_SECTION_NAME,
        repeatCount: 3,
        intervals: [standardOpenInterval(intervalId)],
      },
    ],
    halfwayCue: true,
    countdownCue: DEFAULT_COUNTDOWN_CUE,
    drillSwitchInterval: DEFAULT_DRILL_SWITCH_INTERVAL,
    workBurst: 0,
    burstRest: 0,
    comboSource: "mixed",
  };
}

export interface QuickTimerSettings {
  readonly rounds: number;
  readonly work: Milliseconds;
  readonly rest: Milliseconds;
  readonly prep: Milliseconds;
}

export const QUICK_TIMER_DEFAULTS: QuickTimerSettings = Object.freeze({
  rounds: 8,
  work: 180_000,
  rest: 60_000,
  prep: 10_000,
});

/**
 * Rounds are expressed as `repeatCount` on a single-interval section, not as
 * duplicated intervals: the flattener then drops the final trailing rest for
 * free, so an eight-round timer ends on work.
 *
 * Simple mode always zeroes the rhythm and cadence fields — a quick timer has
 * no combo calls to schedule, and leaving stale guided values on the plan would
 * make the live screen behave differently than the editor showed.
 */
export function quickTimerBlueprint(
  settings: QuickTimerSettings = QUICK_TIMER_DEFAULTS,
): WorkoutPlanBlueprint {
  const sections: WorkoutSectionBlueprint[] = [];
  if (settings.prep > 0) {
    sections.push({
      name: "Prepare",
      repeatCount: 1,
      intervals: [
        {
          title: "Gloves up",
          detail: "Get set",
          workDuration: settings.prep,
          rest: NO_REST,
          target: TIMED_TARGET,
          role: "phase",
          kind: "preparation",
        },
      ],
    });
  }
  sections.push({
    name: "Rounds",
    repeatCount: Math.max(QUICK_TIMER_ROUND_RANGE.minimum, Math.trunc(settings.rounds)),
    intervals: [
      {
        title: "Open round",
        detail: "Your pace • your drill",
        workDuration: settings.work,
        rest:
          settings.rest > 0
            ? { kind: "recovery", duration: settings.rest }
            : NO_REST,
        target: TIMED_TARGET,
        role: "boxingRound",
        kind: "work",
      },
    ],
  });

  return {
    name: PLAN_NAME_FALLBACK.simple,
    mode: "simple",
    sections,
    halfwayCue: true,
    countdownCue: DEFAULT_COUNTDOWN_CUE,
    drillSwitchInterval: 0,
    workBurst: 0,
    burstRest: 0,
  };
}

export const HEAVY_BAG_45_COPY = Object.freeze({
  sectionLabel: "Template",
  sectionDetail: "Ready to customize",
  name: "Heavy Bag 45",
  summary: "Three clear sections · eight guided rounds",
  action: "Use",
  /** "Use" duplicates under this name; the template itself is never edited. */
  duplicateName: "My Heavy Bag 45",
});

interface HeavyBagRound {
  readonly title: string;
  readonly detail: string;
  readonly drill: string;
  readonly theme: GuidedComboTheme;
}

const HEAVY_BAG_45_ROUNDS: readonly [HeavyBagRound, ...HeavyBagRound[]] = [
  { title: "Find your range", detail: "Straight shots • easy rhythm", drill: "1 · 1—2 · move", theme: "straightShots" },
  { title: "Build the base", detail: "Add the lead hook", drill: "1—2—3 · reset", theme: "leadHook" },
  { title: "Body work", detail: "Change levels with your legs", drill: "Head setup · body payoff · reset", theme: "bodyWork" },
  { title: "Block and return", detail: "Read the threat, defend, then answer", drill: "Catch or block · counter · reset", theme: "blocks" },
  { title: "Head movement", detail: "Punch, slip, counter", drill: "1—2 · slip · 2", theme: "slips" },
  { title: "Inside work", detail: "Bring in compact uppercuts", drill: "2—5—2 · reset", theme: "uppercuts" },
  { title: "Angles", detail: "Roll out, return balanced", drill: "1—2—3 · roll · angle", theme: "rolls" },
  { title: "Championship", detail: "Mix your best work", drill: "Freestyle + finish strong", theme: "freestyle" },
];

/** Recovery titles cycle through seven names; the eighth round has no rest. */
const HEAVY_BAG_45_RECOVERY: readonly string[] = Object.freeze([
  "Boxer bounce",
  "Air squats",
  "High knees",
  "Reverse lunges",
  "Plank taps",
  "Slip reps",
  "Roll reps",
]);

function heavyBagRoundBlueprint(round: HeavyBagRound, index: number): WorkoutIntervalBlueprint {
  // The recovery list is one shorter than the round list, which is how the
  // eighth round ends up with no trailing rest at all.
  const recoveryTitle = HEAVY_BAG_45_RECOVERY[index];
  return {
    title: round.title,
    detail: round.detail,
    workDuration: 180_000,
    rest:
      recoveryTitle === undefined
        ? NO_REST
        : {
            kind: "recovery",
            duration: 60_000,
            title: recoveryTitle,
            detail: "Breathe • stay loose",
            drill: recoveryTitle,
          },
    target: TIMED_TARGET,
    drill: round.drill,
    comboTheme: round.theme,
    role: "boxingRound",
    kind: "work",
  };
}

function heavyBagRoundBlueprints(): readonly [
  WorkoutIntervalBlueprint,
  ...WorkoutIntervalBlueprint[],
] {
  const [first, ...rest] = HEAVY_BAG_45_ROUNDS;
  return [
    heavyBagRoundBlueprint(first, 0),
    ...rest.map((round, index) => heavyBagRoundBlueprint(round, index + 1)),
  ];
}

export const HEAVY_BAG_45_BLUEPRINT: WorkoutPlanBlueprint = {
  name: HEAVY_BAG_45_COPY.name,
  mode: "guided",
  sections: [
    {
      name: "Warm-up",
      repeatCount: 1,
      intervals: [
        {
          title: "Wake up the feet",
          detail: "Jump rope • mobility • shadowbox",
          workDuration: 300_000,
          rest: NO_REST,
          target: TIMED_TARGET,
          drill: "Boxer-step jump rope",
          role: "phase",
          kind: "warmup",
        },
      ],
    },
    { name: "Heavy bag", repeatCount: 1, intervals: heavyBagRoundBlueprints() },
    {
      name: "Finish",
      repeatCount: 1,
      intervals: [
        {
          title: "Core finish",
          detail: "Plank series • dead bugs • bicycles",
          workDuration: 240_000,
          rest: NO_REST,
          target: TIMED_TARGET,
          drill: "Alternating core work",
          role: "phase",
          kind: "strength",
        },
        {
          title: "Bring it down",
          detail: "Easy movement • shoulders • breath",
          workDuration: 300_000,
          rest: NO_REST,
          target: TIMED_TARGET,
          drill: "Guided recovery",
          role: "phase",
          kind: "cooldown",
        },
      ],
    },
  ],
  halfwayCue: true,
  countdownCue: DEFAULT_COUNTDOWN_CUE,
  drillSwitchInterval: DEFAULT_DRILL_SWITCH_INTERVAL,
  workBurst: 45_000,
  burstRest: 15_000,
};

/** The four iPad tape add-blocks; each appends a whole new section. */
export type TapeAddBlock = "warmup" | "work" | "recovery" | "repeatGroup";

export const TAPE_ADD_BLOCK_COPY: Readonly<
  Record<TapeAddBlock, { readonly title: string; readonly detail: string }>
> = Object.freeze({
  warmup: { title: "Warm-up", detail: "Preparation and movement" },
  work: { title: "Work interval", detail: "Drill, duration, and cues" },
  recovery: { title: "Recovery", detail: "Active or complete rest" },
  repeatGroup: { title: "Repeat group", detail: "Visible repeated structure" },
});

export function tapeAddBlockBlueprint(block: TapeAddBlock): WorkoutSectionBlueprint {
  switch (block) {
    case "warmup":
      return {
        name: TAPE_ADD_BLOCK_COPY.warmup.title,
        repeatCount: 1,
        intervals: [
          {
            title: TAPE_ADD_BLOCK_COPY.warmup.title,
            detail: TAPE_ADD_BLOCK_COPY.warmup.detail,
            workDuration: 300_000,
            rest: NO_REST,
            target: TIMED_TARGET,
            role: "phase",
            kind: "warmup",
          },
        ],
      };
    case "work":
      return {
        name: "Bag work",
        repeatCount: 1,
        intervals: [
          {
            title: TAPE_ADD_BLOCK_COPY.work.title,
            detail: TAPE_ADD_BLOCK_COPY.work.detail,
            workDuration: 180_000,
            rest: {
              kind: "recovery",
              duration: 60_000,
              title: "Active recovery",
              detail: "Breathe and reset",
            },
            target: TIMED_TARGET,
            role: "boxingRound",
            kind: "work",
          },
        ],
      };
    case "recovery":
      return {
        name: TAPE_ADD_BLOCK_COPY.recovery.title,
        repeatCount: 1,
        intervals: [
          {
            title: TAPE_ADD_BLOCK_COPY.recovery.title,
            detail: TAPE_ADD_BLOCK_COPY.recovery.detail,
            workDuration: 60_000,
            rest: NO_REST,
            target: TIMED_TARGET,
            role: "phase",
            kind: "recovery",
          },
        ],
      };
    case "repeatGroup":
      return {
        name: TAPE_ADD_BLOCK_COPY.repeatGroup.title,
        repeatCount: 3,
        intervals: [
          {
            title: TAPE_ADD_BLOCK_COPY.work.title,
            detail: "Choose a drill in Review",
            workDuration: 180_000,
            rest: { kind: "recovery", duration: 60_000, title: "Recovery" },
            target: TIMED_TARGET,
            role: "boxingRound",
            kind: "work",
          },
        ],
      };
  }
}

// ---------------------------------------------------------------------------
// Effort activities: the planning half of open and manual effort
// ---------------------------------------------------------------------------

export type KnownActivityCategory =
  | "boxing"
  | "conditioning"
  | "endurance"
  | "teamSport"
  | "mobility"
  | "strength"
  | "recovery"
  | "mixed"
  | "other";

const KNOWN_ACTIVITY_CATEGORIES: readonly KnownActivityCategory[] = Object.freeze([
  "boxing",
  "conditioning",
  "endurance",
  "teamSport",
  "mobility",
  "strength",
  "recovery",
  "mixed",
  "other",
] as const);

declare const unknownActivityCategoryBrand: unique symbol;

/**
 * An OPEN enum: a value written by a future release must survive a decode and a
 * re-encode unchanged, because dropping it would silently rewrite a historical
 * record's category to something the user never chose.
 */
export type UnknownActivityCategory = string & {
  readonly [unknownActivityCategoryBrand]: true;
};

export type ActivityCategory = KnownActivityCategory | UnknownActivityCategory;

export function isKnownActivityCategory(
  category: ActivityCategory,
): category is KnownActivityCategory {
  return (KNOWN_ACTIVITY_CATEGORIES as readonly string[]).includes(category);
}

export function activityCategory(raw: string): ActivityCategory {
  return raw as ActivityCategory;
}

/**
 * How a repetition target is judged complete. A rep target is a MANUAL
 * prescription: the app never substitutes a fabricated duration for it, so the
 * completion signal has to come from the user or from a visible time cap.
 */
export type ManualCompletionPolicy = "userConfirmed" | "timeCapped";

/**
 * What the plan asks for. `repetitions` never implies a duration, and
 * `openEnded` never implies a target — the count-up clock has nothing to
 * compare against, which is exactly why it is its own case.
 */
export type EffortTarget =
  | { readonly kind: "duration"; readonly duration: Milliseconds }
  | {
      readonly kind: "repetitions";
      readonly count: number;
      readonly completion: ManualCompletionPolicy;
    }
  | { readonly kind: "openEnded" };

export const OPEN_ENDED_TARGET: EffortTarget = Object.freeze({ kind: "openEnded" } as const);

export interface ActivityDefinition {
  /** Fixed for catalog items; a NEW uuid for every custom draft. */
  readonly id: ActivityId;
  readonly schemaVersion: number;
  /** Stable app catalog identity; absent for custom activities. */
  readonly catalogKey?: string;
  /** Absent for custom, >= 1 for catalog. */
  readonly catalogRevision?: number;
  readonly name: string;
  readonly category: ActivityCategory;
  readonly defaultTarget: EffortTarget;
  readonly isUserCreated: boolean;
}

/**
 * The copy written into a session at the moment it happened. Editing an old
 * record preserves a RETIRED snapshot rather than re-resolving today's catalog,
 * so a catalog change can never rewrite what the user actually did.
 */
export type ActivitySnapshot = ActivityDefinition;

export const ACTIVITY_SCHEMA_VERSION = 1;
export const CATALOG_REVISION = 1;

export type EffortCatalogKey = "boxing" | "cycling" | "basketball" | "stretching" | "gym";

function catalogActivity(
  uuid: string,
  catalogKey: EffortCatalogKey,
  name: string,
  category: KnownActivityCategory,
): ActivityDefinition {
  return {
    id: activityId(uuid),
    schemaVersion: ACTIVITY_SCHEMA_VERSION,
    catalogKey,
    catalogRevision: CATALOG_REVISION,
    name,
    category,
    defaultTarget: OPEN_ENDED_TARGET,
    isUserCreated: false,
  };
}

/**
 * Persisted product identifiers. Changing one of these uuids would split a
 * single catalog activity into unrelated historical values, so they are fixed
 * forever regardless of what the display name becomes.
 */
export const EFFORT_ACTIVITY_CATALOG: Readonly<Record<EffortCatalogKey, ActivityDefinition>> =
  Object.freeze({
    boxing: catalogActivity("F1000000-0000-5000-8000-000000000001", "boxing", "Boxing", "boxing"),
    cycling: catalogActivity(
      "F1000000-0000-5000-8000-000000000002",
      "cycling",
      "Cycling",
      "endurance",
    ),
    basketball: catalogActivity(
      "F1000000-0000-5000-8000-000000000003",
      "basketball",
      "Basketball",
      "teamSport",
    ),
    stretching: catalogActivity(
      "F1000000-0000-5000-8000-000000000004",
      "stretching",
      "Stretching",
      "mobility",
    ),
    gym: catalogActivity("F1000000-0000-5000-8000-000000000005", "gym", "Gym work", "strength"),
  });

/**
 * The pre-catalog boxing record. Existing sessions reference this id directly,
 * so it stays addressable even though nothing new is ever created with it.
 */
export const LEGACY_BOXING_WORKOUT_ACTIVITY: ActivityDefinition = {
  id: activityId("9DDBCB3D-FA4F-5D69-86C9-DC2A1D11F5B8"),
  schemaVersion: ACTIVITY_SCHEMA_VERSION,
  name: "Boxing workout",
  category: "boxing",
  defaultTarget: OPEN_ENDED_TARGET,
  isUserCreated: false,
};

/** The count-up clock does not offer boxing: boxing runs as a structured plan. */
export const OPEN_EFFORT_ACTIVITY_KEYS: readonly EffortCatalogKey[] = Object.freeze([
  "cycling",
  "basketball",
  "stretching",
  "gym",
] as const);

/** Retrospective entry additionally offers boxing, since it already happened. */
export const MANUAL_EFFORT_ACTIVITY_KEYS: readonly EffortCatalogKey[] = Object.freeze([
  "boxing",
  "cycling",
  "basketball",
  "stretching",
  "gym",
] as const);

export const MAXIMUM_CUSTOM_ACTIVITY_NAME_LENGTH = 60;

export type ActivityNameValidation =
  | { readonly status: "valid"; readonly name: string }
  | { readonly status: "empty" }
  | { readonly status: "tooLong"; readonly length: number }
  | { readonly status: "controlCharacters" };

export const ACTIVITY_NAME_STATUS_COPY = Object.freeze({
  valid: "Ready to track",
  empty: "Enter an activity name.",
  tooLong: "Use 60 characters or fewer.",
  controlCharacters: "Activity names can't contain line breaks or control characters.",
  retired: "That activity is no longer available. Choose it again.",
  invalid: "Choose a valid activity.",
});

/** Grapheme clusters, not code units: one emoji is one character to the user. */
const graphemeSegmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });

function graphemeCount(value: string): number {
  return Array.from(graphemeSegmenter.segment(value)).length;
}

/**
 * Order matters and is not incidental: control characters are rejected BEFORE
 * trimming, so a name that is only whitespace and newlines reports the control
 * problem rather than the empty one, and the length check runs on the trimmed
 * text. Internal spacing and case are preserved exactly as typed.
 */
export function validateActivityName(raw: string): ActivityNameValidation {
  if (/[\u0000-\u001F\u007F-\u009F\u2028\u2029]/u.test(raw)) {
    return { status: "controlCharacters" };
  }
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { status: "empty" };
  const length = graphemeCount(trimmed);
  if (length > MAXIMUM_CUSTOM_ACTIVITY_NAME_LENGTH) return { status: "tooLong", length };
  return { status: "valid", name: trimmed };
}

/**
 * A custom activity always gets a brand-new id, so the same typed text never
 * merges with an earlier custom identity — two sessions the user thinks of as
 * "Yoga" stay distinguishable if they were entered as separate activities.
 */
export function customActivityDefinition(name: string, id: ActivityId): ActivityDefinition {
  return {
    id,
    schemaVersion: ACTIVITY_SCHEMA_VERSION,
    name,
    category: "other",
    defaultTarget: OPEN_ENDED_TARGET,
    isUserCreated: true,
  };
}

// ---------------------------------------------------------------------------
// Open-effort and manual-effort plan shapes
// ---------------------------------------------------------------------------

/** Sub-minute manual values are impossible; the fields are hours and minutes. */
export const MANUAL_EFFORT_RESOLUTION: Milliseconds = 60_000;

/** Combined wall duration ceiling. Over-ceiling values are rejected, never clamped. */
export const MANUAL_EFFORT_MAXIMUM_WALL: Milliseconds = 7 * 24 * 60 * 60 * 1_000;

/**
 * Pause is disclosed or it is not; there is no "pause of zero". The union keeps
 * a blanked pause field from being recorded as a real, measured zero.
 */
export type ManualEffortDosage =
  | { readonly kind: "activeOnly"; readonly active: Milliseconds }
  | {
      readonly kind: "activePlusPause";
      readonly active: Milliseconds;
      readonly pause: Milliseconds;
    };

export function manualEffortWallDuration(dosage: ManualEffortDosage): Milliseconds {
  return dosage.kind === "activePlusPause" ? dosage.active + dosage.pause : dosage.active;
}

export type ManualEffortDosageIssue =
  | { readonly kind: "hoursNotWhole" }
  | { readonly kind: "minutesNotWhole" }
  | { readonly kind: "minutesOutOfRange" }
  | { readonly kind: "durationNotPositive" }
  | { readonly kind: "wallDurationTooLong"; readonly wall: Milliseconds }
  | { readonly kind: "pauseNotPositive" };

export const MANUAL_EFFORT_DOSAGE_COPY = Object.freeze({
  minutesOutOfRange: "minutes from 0 through 59",
  pauseCaption: "Only add this when you remember a positive amount of stopped time.",
  savedFootnote: "Active time is saved as user-entered, not measured.",
});

/**
 * Empty text is zero; anything that is not a whole number is -1. Returning a
 * sentinel instead of coercing is deliberate: typing "6o" must fail validation
 * and keep the user's text on screen, not silently become 60.
 */
export function parseManualDurationField(text: string): number {
  const trimmed = text.trim();
  if (trimmed.length === 0) return 0;
  if (!/^\d+$/.test(trimmed)) return -1;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isSafeInteger(parsed) ? parsed : -1;
}

export interface ManualEffortDosageInput {
  readonly hours: number;
  readonly minutes: number;
  /** Present only while the pause row is disclosed. */
  readonly pauseHours?: number;
  readonly pauseMinutes?: number;
}

export type ManualEffortDosageValidation =
  | { readonly status: "valid"; readonly dosage: ManualEffortDosage }
  | { readonly status: "invalid"; readonly issues: readonly ManualEffortDosageIssue[] };

/**
 * Whole hours and 0...59 minutes, a strictly positive total, a strictly
 * positive disclosed pause, and a wall duration inside the ceiling. Nothing
 * here rounds or clamps: a rejected entry stays on screen exactly as typed so
 * the user can see what the app disagreed with.
 */
export function validateManualEffortDosage(
  input: ManualEffortDosageInput,
): ManualEffortDosageValidation {
  const issues: ManualEffortDosageIssue[] = [];

  if (!isWholeNumber(input.hours) || input.hours < 0) issues.push({ kind: "hoursNotWhole" });
  if (!isWholeNumber(input.minutes) || input.minutes < 0) {
    issues.push({ kind: "minutesNotWhole" });
  } else if (input.minutes > 59) {
    issues.push({ kind: "minutesOutOfRange" });
  }

  const totalMinutes = input.hours * 60 + input.minutes;
  if (issues.length === 0 && totalMinutes <= 0) issues.push({ kind: "durationNotPositive" });

  const discloses = input.pauseHours !== undefined || input.pauseMinutes !== undefined;
  const pauseHours = input.pauseHours ?? 0;
  const pauseMinutes = input.pauseMinutes ?? 0;
  let pause: Milliseconds = 0;
  if (discloses) {
    if (!isWholeNumber(pauseHours) || pauseHours < 0) issues.push({ kind: "hoursNotWhole" });
    if (!isWholeNumber(pauseMinutes) || pauseMinutes < 0) {
      issues.push({ kind: "minutesNotWhole" });
    } else if (pauseMinutes > 59) {
      issues.push({ kind: "minutesOutOfRange" });
    }
    const pauseTotal = pauseHours * 60 + pauseMinutes;
    if (issues.length === 0 && pauseTotal <= 0) issues.push({ kind: "pauseNotPositive" });
    pause = pauseTotal * MANUAL_EFFORT_RESOLUTION;
  }

  const active: Milliseconds = totalMinutes * MANUAL_EFFORT_RESOLUTION;
  const wall = active + pause;
  if (issues.length === 0 && wall > MANUAL_EFFORT_MAXIMUM_WALL) {
    issues.push({ kind: "wallDurationTooLong", wall });
  }

  if (issues.length > 0) return { status: "invalid", issues };
  return {
    status: "valid",
    dosage: discloses ? { kind: "activePlusPause", active, pause } : { kind: "activeOnly", active },
  };
}

/**
 * The three session shapes, as they exist BEFORE anything runs. Structured
 * carries a derived plan; open effort carries only an activity, because a
 * count-up clock has no prescription at all; manual carries the activity plus
 * the dosage the user remembers. All three archive into one canonical session,
 * which is only possible because none of them pretends to be another.
 */
export type EffortPlan =
  | { readonly shape: "structured"; readonly session: SessionPlan }
  | { readonly shape: "openEffort"; readonly activity: ActivitySnapshot }
  | {
      readonly shape: "manual";
      readonly activity: ActivitySnapshot;
      readonly dosage: ManualEffortDosage;
    };

export function structuredEffortPlan(plan: WorkoutPlan): EffortPlan {
  return { shape: "structured", session: deriveSessionPlan(plan) };
}

export function openEffortPlan(activity: ActivitySnapshot): EffortPlan {
  return { shape: "openEffort", activity };
}

export function manualEffortPlan(
  activity: ActivitySnapshot,
  dosage: ManualEffortDosage,
): EffortPlan {
  return { shape: "manual", activity, dosage };
}

export const EFFORT_MODULE_COPY = Object.freeze({
  cardTitle: "Effort",
  cardSubtitle: "Any movement, over time",
  cardBody: "Run a clock while you move, or add work you already finished.",
  startNow: "Start now",
  addPast: "Add past effort",
  setupEyebrow: "EFFORT CLOCK",
  setupTitle: "What are you doing?",
  setupBody: "Start a count-up clock now. Add a reflection when you finish.",
  otherActivityLabel: "Activity name",
  manualEyebrow: "PAST EFFORT",
  manualTitle: "Add what you did",
  manualEditEyebrow: "EDIT EFFORT",
  manualEditTitle: "Correct the record",
  manualBody:
    "Enter the effort you remember. Time is recorded as your estimate, not as measured data.",
  activeDurationLabel: "Time active",
  pauseDurationLabel: "Time stopped",
  addPauseTime: "Add pause time",
  removePauseTime: "Remove pause time",
});
